import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileText } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const ReadabilityChecker = () => {
  const [content, setContent] = useState<string>("");

  const result = useMemo(() => {
    if (!content.trim()) return null;
    
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const words = content.split(/\s+/).filter(w => w.length > 0);
    const syllables = words.reduce((acc, word) => acc + countSyllables(word), 0);
    
    const avgWordsPerSentence = words.length / Math.max(sentences.length, 1);
    const avgSyllablesPerWord = syllables / Math.max(words.length, 1);
    
    // Flesch Reading Ease
    const flesch = 206.835 - (1.015 * avgWordsPerSentence) - (84.6 * avgSyllablesPerWord);
    
    return { flesch: Math.max(0, Math.min(100, flesch)), words: words.length, sentences: sentences.length };
  }, [content]);

  const countSyllables = (word: string): number => {
    word = word.toLowerCase().replace(/[^a-z]/g, '');
    if (word.length <= 3) return 1;
    const vowels = word.match(/[aeiouy]+/g);
    return vowels ? vowels.length : 1;
  };

  const getGrade = (score: number) => {
    if (score >= 90) return { label: "Very Easy", grade: "5th Grade", color: "text-green-600" };
    if (score >= 80) return { label: "Easy", grade: "6th Grade", color: "text-green-500" };
    if (score >= 70) return { label: "Fairly Easy", grade: "7th Grade", color: "text-yellow-600" };
    if (score >= 60) return { label: "Standard", grade: "8-9th Grade", color: "text-yellow-500" };
    if (score >= 50) return { label: "Fairly Difficult", grade: "10-12th Grade", color: "text-orange-500" };
    if (score >= 30) return { label: "Difficult", grade: "College", color: "text-red-500" };
    return { label: "Very Difficult", grade: "College Graduate", color: "text-red-600" };
  };

  const relatedTools = getRelatedTools("readability-checker");

  return (
    <ToolPageLayout
      title="Readability Checker"
      description="Analyze the readability of your content using the Flesch Reading Ease score."
      metaDescription="Check how easy your content is to read using proven readability formulas. Ensure your content is accessible to your target audience for better engagement and SEO."
      icon={FileText}
      slug="readability-checker"
      formula={{
        equation: "Flesch = 206.835 - (1.015 × ASL) - (84.6 × ASW)",
        variables: [
          { name: "ASL", description: "Average Sentence Length (words per sentence)" },
          { name: "ASW", description: "Average Syllables per Word" },
        ]
      }}
      example={{
        inputs: [{ label: "Sample Text", value: "300 words blog post" }],
        output: "65 Flesch Score (Standard)",
        interpretation: "A score of 65 is readable by 8th-9th graders. Ideal for most web content."
      }}
      improvements={[
        "Use shorter sentences (aim for 15-20 words)",
        "Choose simpler words with fewer syllables",
        "Break complex ideas into multiple sentences",
        "Use active voice instead of passive",
        "Add subheadings to break up long sections",
        "Read your content aloud to check flow",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "SEO Services", href: "/seo" }}
    >
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Enter Content</CardTitle>
            <CardDescription>Paste your text to analyze</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                placeholder="Paste your content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[300px]"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Readability Score</CardTitle>
            <CardDescription>Flesch Reading Ease analysis</CardDescription>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                <div className="text-center p-6 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm text-muted-foreground mb-1">Flesch Score</p>
                  <p className={`text-4xl font-bold ${getGrade(result.flesch).color}`}>
                    {result.flesch.toFixed(0)}
                  </p>
                  <p className={`text-sm mt-2 ${getGrade(result.flesch).color}`}>
                    {getGrade(result.flesch).label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {getGrade(result.flesch).grade} reading level
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-semibold text-foreground">{result.words}</p>
                    <p className="text-xs text-muted-foreground">Words</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-semibold text-foreground">{result.sentences}</p>
                    <p className="text-xs text-muted-foreground">Sentences</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">
                Enter content to analyze readability.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </ToolPageLayout>
  );
};

export default ReadabilityChecker;
